import React from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Box, LinearProgress, Stack, Chip } from '@mui/material'
import SyncIcon from '@mui/icons-material/Sync'
import CloudDoneIcon from '@mui/icons-material/CloudDone'
import CloudOffIcon from '@mui/icons-material/CloudOff'
import { useOfflineMode } from '@/providers/OfflineModeProvider'

interface SyncDialogProps {
  open: boolean
  onClose: () => void
}

export default function SyncDialog({ open, onClose }: SyncDialogProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { isOfflineMode, timeUntilExpiry } = useOfflineMode()

  // Temporary translations until proper i18n is added
  const translations = {
    title: 'Data Synchronization',
    status: 'Connection Status',
    online: 'Online',
    offline: 'Offline',
    offlineAccess: 'Offline Access Remaining',
    noExpiry: 'No expiration',
    expired: 'Expired',
    timeRemaining: '{{days}} days, {{hours}} hours',
    hoursRemaining: '{{hours}} hours',
    expiryWarning: 'Please sync soon to maintain offline access',
    offlineDescription: 'You are currently working offline. Your changes will be synchronized when you reconnect to the internet.',
    onlineDescription: 'You are online. You can synchronize your offline data with the server.',
    syncDescription: 'Click below to manage data synchronization:',
    goToSync: 'Go to Sync Page',
  }

  const tr = (key: string, params?: any) => {
    let text = translations[key.split('.').pop() as keyof typeof translations] || key
    if (params) {
      Object.keys(params).forEach((param) => {
        text = text.replace(`{{${param}}}`, params[param])
      })
    }
    return text
  }

  const handleNavigateToSync = () => {
    onClose()
    navigate('/offline/sync')
  }

  const formatTimeRemaining = (hours: number | null) => {
    if (hours === null) return tr('sync.dialog.noExpiry')
    if (hours <= 0) return tr('sync.dialog.expired')

    const days = Math.floor(hours / 24)
    const remainingHours = Math.floor(hours % 24)

    if (days > 0) {
      return tr('sync.dialog.timeRemaining', { days, hours: remainingHours })
    }
    return tr('sync.dialog.hoursRemaining', { hours: remainingHours })
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" spacing={1}>
          <SyncIcon />
          <Typography variant="h6">{tr('sync.dialog.title')}</Typography>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={3}>
          <Box>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Typography variant="body2" color="text.secondary">
                {tr('sync.dialog.status')}:
              </Typography>
              {navigator.onLine ? (
                <Chip icon={<CloudDoneIcon />} label={tr('sync.dialog.online')} color="success" size="small" />
              ) : (
                <Chip icon={<CloudOffIcon />} label={tr('sync.dialog.offline')} color="warning" size="small" />
              )}
            </Stack>
          </Box>

          {isOfflineMode && (
            <Box>
              <Typography variant="body2" color="text.secondary">
                {tr('sync.dialog.offlineAccess')}:
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {formatTimeRemaining(timeUntilExpiry)}
              </Typography>
              {timeUntilExpiry !== null && timeUntilExpiry < 12 && (
                <Typography variant="caption" color="warning.main">
                  {tr('sync.dialog.expiryWarning')}
                </Typography>
              )}
            </Box>
          )}

          <Typography variant="body2">{isOfflineMode ? tr('sync.dialog.offlineDescription') : tr('sync.dialog.onlineDescription')}</Typography>

          {navigator.onLine && (
            <Stack spacing={2}>
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {tr('sync.dialog.syncDescription')}
                </Typography>
                <Button variant="contained" startIcon={<SyncIcon />} onClick={handleNavigateToSync} fullWidth>
                  {tr('sync.dialog.goToSync')}
                </Button>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Have poor connectivity? Work offline to avoid interruptions.
                </Typography>
                <Button
                  variant="outlined"
                  onClick={() => {
                    onClose()
                    navigate('/offline/patients')
                  }}
                  fullWidth
                >
                  Switch to Offline Mode
                </Button>
              </Box>
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common:button.close.label')}</Button>
      </DialogActions>
    </Dialog>
  )
}
